import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { slugify } from "../../utils/slug";
import "./ResultCards.css";
import "../../assets/css/mobile.css";

/* Drawn, not fetched. The card pointed at "/placeholder.png", which does
   not exist in public/ - so every mould without a photo rendered the
   browser's broken-image icon. Same thin-stroke mark the listing page
   uses for the same state. */
function NoPhotoMark() {
  return (
    <svg viewBox="0 0 40 32" width="34" height="28" aria-hidden="true" focusable="false">
      <rect x="1" y="1" width="38" height="30" rx="3" fill="none" stroke="currentColor" strokeWidth="1.2" />
      <path d="M6 24l8.5-9 5.5 6 4.5-4.5L34 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="14" cy="10.5" r="2.4" fill="none" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  );
}

const ResultCards = ({
  _id = "",
  vendor = "",
  vendorSlug = "",
  linkVendor = true,
  title = "",
  description = "",
  color = "#ccc",
  /* Empty, not "0". These defaulted to the string "0", which is truthy, so
     the `width || "N/A"` fallback below could never fire and a mould with
     no recorded dimensions displayed "W 0  H 0". A buyer cannot tell that
     from a mould actually measuring zero. */
  width = "",
  height = "",
  createdDate = "",
  shape = "",
  mass = "",
  images = "",
  casetype = "",
  fromMeasure = false,
  measureImage = "",
}) => {
  const navigate = useNavigate();
  const imageSrc = Array.isArray(images) ? images[0] : images;

  const trimmedColor = typeof color === "string" ? color.trim() : "";
  const renderableColor =
    trimmedColor &&
    typeof CSS !== "undefined" &&
    typeof CSS.supports === "function" &&
    CSS.supports("color", trimmedColor)
      ? trimmedColor
      : null;

const handleCardClick = () => {
  const queryParams = new URLSearchParams({
    fromMeasure: fromMeasure ? "true" : "false",
  });

  // Add nostril width to URL parameters if available
  const nostrilWidth = localStorage.getItem('capturedWidthMM');
  if (nostrilWidth) {
    queryParams.set('nostrilWidth', nostrilWidth);
  }

  navigate(`/product-description/${_id}?${queryParams.toString()}`);
};


  return (
    /* role + tabIndex + Enter/Space: the card navigates on click, so
       without these it was mouse-only - a keyboard or screen-reader user
       could not open a single search result. */
    <article
      className="result-card"
      onClick={handleCardClick}
      role="link"
      tabIndex={0}
      aria-label={`${title || "Untitled"}${vendor ? `, ${vendor}` : ""}`}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleCardClick();
        }
      }}
    >
      <div className="results-top-section">
        {/* mm, because every number on this card is a millimetre and a bare
            "24" beside a bare "11" says nothing. Both absent is one state,
            not two "N/A"s. */}
        {width || height ? (
          <dl className="result-dims">
            <div>
              <dt>W</dt>
              <dd>{width ? `${width} mm` : "\u2014"}</dd>
            </div>
            <div>
              <dt>H</dt>
              <dd>{height ? `${height} mm` : "\u2014"}</dd>
            </div>
          </dl>
        ) : (
          <span className="result-dims-none">No dimensions</span>
        )}
        {/* stopPropagation: the card itself navigates to the product, so without
            it a click on the vendor name would fire both navigations. */}
        <p className="result-vendor">
          {vendor ? (
            linkVendor ? (
              <Link
                to={`/vendor/${vendorSlug || slugify(vendor)}`}
                onClick={(e) => e.stopPropagation()}
              >
                {vendor}
              </Link>
            ) : (
              vendor
            )
          ) : (
            "Unknown Vendor"
          )}
        </p>
      </div>

      <div className="card-img">
        {imageSrc ? (
          <img
            loading="lazy"
            src={imageSrc}
            alt={title ? `${title} tooth mould` : "Tooth mould"}
          />
        ) : (
          <span className="card-img-empty">
            <NoPhotoMark />
            <span>No photo</span>
          </span>
        )}
      </div>

      <div className="description-section">
        <div className="description-section-text">
          <p className="result-title">{title || "Untitled"}</p>
          {description && <p className="result-desc">{description}</p>}
        </div>

        {/* The API returns shade names ("Grayish", "Upper"), not CSS colours.
            Feeding those straight to background-color makes the browser drop
            the declaration, so the swatch rendered as an empty bordered box.
            Only paint a swatch when the value is a colour the browser can
            actually resolve; otherwise show the name, which is the real
            information anyway. */}
        {renderableColor ? (
          <span
            className="color-swatch"
            style={{ backgroundColor: renderableColor }}
            title={`Shade ${color}`}
          />
        ) : color ? (
          <span className="color-name" title={`Shade ${color}`}>
            {color}
          </span>
        ) : null}
      </div>

      {fromMeasure && measureImage && (
        <div className="measure-image-preview">
          <p>Your measured face</p>
          <img
            loading="lazy"
            src={measureImage}
            alt="Your captured face measurement"
            className="measure-thumb"
          />
        </div>
      )}
    </article>
  );
};

export default ResultCards;
